import type { NodeDefinition } from '../../../types/node-definition';

/**
 * Execute Workflow Tool Node
 *
 * This node allows AI Agents to execute sub-workflows as tools.
 * Similar to Execute Workflow, but designed specifically for AI Agent tool use
 * via the tool handle.
 *
 * Parameters (defined in schema.ts):
 * - workflowId: The workflow to execute
 * - toolName: Custom name for the tool (optional)
 * - toolDescription: Description shown to AI Agent
 * - inputSchema: JSON Schema for input parameters (optional)
 */
const definition: NodeDefinition = {
  type: 'execute-workflow-tool',
  name: 'Execute Workflow Tool',
  category: 'tools',
  description: 'Execute a workflow as a tool for AI Agents. Configure a description to help the AI understand when to invoke this workflow.',
  icon: 'Workflow',
  color: 'green',
  version: '1.0.0',

  // Mark as sub-node that connects to AI Agent's tools anchor
  isSubNode: true,
  subNodeConfig: {
    category: 'tool',
    requiredFor: ['aiAgent'],
    multipleAllowed: true,
    description: 'Execute a workflow as an AI Agent tool'
  },

  group: ['ai', 'transform'],

  defaults: {
    name: 'Execute Workflow Tool',
    color: 'green'
  },

  subtitle: '={{ $parameter.workflowId ? "Workflow Tool" : "Select workflow" }}',

  // Sub-nodes don't have traditional inputs/outputs
  inputs: [],
  outputs: [],

  tags: ['workflow', 'execute', 'tool', 'agent', 'subworkflow', 'ai'],

  comingSoon: false,

  toolCapable: {
    enabled: true,
    name: 'execute_workflow',
    description: 'Execute a sub-workflow',
    allowManualDescription: true,
    parameters: {
      input: {
        toolParamName: 'input',
        description: 'Input data to pass to the workflow',
        required: false,
        type: 'object'
      }
    },
    outputMapping: {
      result: 'The result returned by the workflow',
      workflowId: 'The ID of the executed workflow',
      executionId: 'The execution ID for tracking'
    }
  }
};

export default definition;
